import { gql } from '@apollo/client';
import { put, retry, select, takeEvery } from 'redux-saga/effects';
import ActionTypes from '../../../../Data/ActionTypes';
import SagaStatus from '../../../../Data/SagaStatus';
import ValidationStatus from '../../../../Data/ValidationStatus';
import { query } from '../../../../Services/Gateway/Apollo';
import {
  setComponentValidationStatus,
  setPostcodeValidationConstraint,
  setValidationConstraintStatus,
} from '../../slices/components';
import { setComponentIdToSagaStatus } from '../../slices/dataMappings';
import { handleAnswerQuestionSideEffectsSaga } from '../../../../Hooks/actionCreators';
import { isFormShowingSupplierCardsConditionallyOnZipCode } from '../../../../Helpers/conditionality';
import {
  checkIsPostcodeBlockingSubmission,
  checkPostcodeAgainstSubcategory,
} from './validatePostcodeHelpers';
import {
  SUBMISSION_BLOCK_MESSAGES,
  SUBMISSION_BLOCK_TYPES,
} from '../../../../Data/Constants';
import { setSubmissionBlock } from '../../slices/submission';

export const VALIDATE_POSTCODE = gql`
  query ValidatePostcode($postcode: PostcodeInput!) {
    validatePostcode(postcode: $postcode) {
      isValidCode
    }
  }
`;

const validatePostcodeQuery = async ({ variables }) => {
  try {
    const response = await query({
      query: VALIDATE_POSTCODE,
      variables,
    });

    return response.data.validatePostcode.isValidCode;
  } catch (error) {
    return false;
  }
};

/**
 * Worker - requested by below function and carries out any async work
 */
export function* worker(action) {
  const { componentId, value, countryCode, iFrameId, formId } = action.payload;
  const { components, formConfig } = yield select();
  const component = components[`component_${componentId}`];

  yield put(
    setComponentIdToSagaStatus({
      componentId,
      status: SagaStatus.PENDING,
    })
  );

  const requestArguments = {
    postcode: {
      country: countryCode,
      code: value,
    },
  };

  const SECOND = 1000;

  yield put(
    setPostcodeValidationConstraint({
      componentId,
      status: ValidationStatus.PENDING,
    })
  );
  const isValid = yield retry(3, 10 * SECOND, validatePostcodeQuery, {
    variables: requestArguments,
  });

  if (isValid) {
    const isPostcodeBlockingSubmission =
      yield checkIsPostcodeBlockingSubmission({
        postcode: value,
        formConfig,
      });
    yield put(
      setSubmissionBlock({
        type: SUBMISSION_BLOCK_TYPES.INPUT_VALUE,
        identifier: componentId,
        page: component.component.page,
        isBlockingSubmission: isPostcodeBlockingSubmission,
        message: SUBMISSION_BLOCK_MESSAGES.INVALID_POSTCODE,
      })
    );
    if (isFormShowingSupplierCardsConditionallyOnZipCode(formId)) {
      yield checkPostcodeAgainstSubcategory({
        postcode: value,
        components,
        subcategoryId: formConfig.subcategoryId,
      });
    }
    yield put(
      setValidationConstraintStatus({
        componentId,
        status: ValidationStatus.VALID,
        constraintName: 'postcode',
      })
    );
    /**
     * validate the rest of the components before the handle expose event
     */
    yield put(
      setComponentValidationStatus({
        componentId,
        countryCode,
        iFrameId,
        formId,
      })
    );
    // Dispatch the action to trigger the side-effects saga
    yield put(handleAnswerQuestionSideEffectsSaga({ componentId }));
  } else {
    yield put(
      setValidationConstraintStatus({
        componentId,
        status: ValidationStatus.INVALID,
        constraintName: 'postcode',
      })
    );
    yield put(
      setComponentValidationStatus({
        componentId,
        countryCode,
        iFrameId,
        formId,
      })
    );
  }
  yield put(
    setComponentIdToSagaStatus({
      componentId,
      status: SagaStatus.COMPLETE,
    })
  );
}

export const validatePostcodeSaga = ({
  countryCode,
  value,
  componentId,
  iFrameId,
  formId,
}) => ({
  type: ActionTypes.VALIDATE_POSTCODE,
  payload: { countryCode, value, componentId, iFrameId, formId },
});

/**
 * Listens for every VALIDATE_POSTCODE action
 */
export default function* validatePostcode() {
  yield takeEvery(ActionTypes.VALIDATE_POSTCODE, worker);
}
